import { createContext, useContext, useState, useEffect } from "react";
import axios from "axios";
import { rememberToken } from "@/lib/biometric";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const AuthContext = createContext(null);

// Configure axios globally
axios.defaults.withCredentials = true;

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);       // null = checking, false = not auth
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      const res = await axios.get(`${API}/auth/me`);
      setUser(res.data);
    } catch {
      setUser(false);
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password, module = "gst") => {
    const res = await axios.post(`${API}/auth/login`, { email, password, module });
    setUser(res.data);
    // If biometric unlock is already enrolled on this device, cache the
    // freshest token so the next fingerprint unlock can resume the session.
    if (res.data?.access_token) rememberToken(res.data.access_token);
    return res.data;
  };

  const register = async (email, password, name, company_name) => {
    const res = await axios.post(`${API}/auth/register`, { email, password, name, company_name });
    setUser(res.data);
    if (res.data?.access_token) rememberToken(res.data.access_token);
    return res.data;
  };

  const logout = async () => {
    await axios.post(`${API}/auth/logout`);
    // Drop the cached biometric token so a stolen device can't replay it.
    rememberToken(null);
    setUser(false);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
